import {
  Button,
  CloseButton,
  Divider,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuList,
  Text,
  useDisclosure
} from '@chakra-ui/react'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { TbReload } from 'react-icons/tb'

import CalendarIcon from '@/ui/assets/CalendarIcon'
import FilterIcon from '@/ui/assets/FilterIcon'
import ShearchLoupeIcon from '@/ui/assets/ShearchLoupeIcon'
import SemesterInput from '@/ui/components/Form/SemesterInput'
import WeekInput from '@/ui/components/Form/WeekInput'
import YearInput from '@/ui/components/Form/YearInput'

import { useCAAMetricsContext } from '../context/CAAMetricsContext'

export default function CAAMetricsFilter() {
  const { t } = useTranslation()

  const {
    isOpen: isFilterMenuOpen,
    onToggle: onFilterMenuToggle,
    onClose: onFilterMenuClose
  } = useDisclosure()

  const {
    filter,
    setWeek,
    setMonth,
    setSemester,
    setYear,
    setPersonaName,
    periodType,
    showPatient,
    metricsQuery
  } = useCAAMetricsContext()

  const isFilterActive = Boolean(
    filter.week ||
      filter.month ||
      filter.semester ||
      filter.year ||
      filter.personaName
  )
  const bg = isFilterActive ? 'custom.blue.darker' : undefined
  const color = isFilterActive ? '#FFFFFF' : undefined

  return (
    <Flex
      wrap={'wrap'}
      justify={'space-between'}
      align={'center'}
      w={'full'}
      borderWidth={'1px'}
      p={2}
      borderRadius={'4px'}
    >
      <Flex
        flex={1}
        color={'custom.gray.200'}
        fontSize={'16'}
        fontWeight={500}
        gap={2}
      >
        {periodType === 'WEEK' ? (
          <Flex align={'center'} gap={1}>
            <CalendarIcon boxSize={'18px'} />
            <Text>
              {moment(filter.week).startOf('week').format('L')} -{' '}
              {moment(filter.week).endOf('week').format('L')}
            </Text>
          </Flex>
        ) : periodType === 'MONTH' ? (
          <Flex align={'center'} gap={1}>
            <CalendarIcon boxSize={'18px'} />
            <Text>
              {moment(filter.month)
                .format('MMMM YYYY')
                .replace(/\w/, (r) => r.toUpperCase())}
            </Text>
          </Flex>
        ) : periodType === 'SEMESTER' ? (
          <Flex align={'center'} gap={1}>
            <CalendarIcon boxSize={'18px'} />
            <Text>
              {t('caaMetrics.filter.presentation.year')} {filter.year},{' '}
              {filter.semester === '1' ? '1º' : '2º'}{' '}
              {t('caaMetrics.filter.presentation.semester')}
            </Text>
          </Flex>
        ) : periodType === 'YEAR' ? (
          <Flex align={'center'} gap={1}>
            <CalendarIcon boxSize={'18px'} />
            <Text>
              {t('caaMetrics.filter.presentation.year')} {filter.year}
            </Text>
          </Flex>
        ) : (
          <></>
        )}
        {filter.personaName && (
          <>
            ,
            <Flex align={'baseline'} gap={1}>
              <ShearchLoupeIcon boxSize={'18px'} />
              <Text>{filter.personaName}</Text>
            </Flex>
          </>
        )}
      </Flex>
      <Flex gap={4}>
        {!!metricsQuery && (
          <Button
            backgroundColor={'custom.blue.darker'}
            color="white"
            fontSize={'20px'}
            isLoading={metricsQuery.isFetching}
            isDisabled={metricsQuery.isFetching}
            onClick={() => metricsQuery.refetch()}
          >
            <TbReload />
          </Button>
        )}
        <Menu isOpen={isFilterMenuOpen} onClose={onFilterMenuClose}>
          <MenuButton
            as={Button}
            variant="outline"
            onClick={onFilterMenuToggle}
            rightIcon={<FilterIcon color={color} />}
            borderWidth="1px"
            borderRadius="md"
            py="2"
            px="3"
            w="130px"
            {...(bg && { bg })}
            _hover={{ textDecoration: 'none' }}
            _active={{ textDecoration: 'none' }}
          >
            <Text w="fit-content" fontWeight="500" color={color}>
              {t('therapists.documents.request.filter.button')}
            </Text>
          </MenuButton>

          <MenuList
            w={'390px'}
            px="4"
            pb="4"
            display="flex"
            flexDir="column"
            gap="4"
            zIndex={2}
          >
            <Flex
              justifyContent={'space-between'}
              w={'full'}
              alignItems={'center'}
              h={'fit-content'}
              mb={'-2'}
            >
              <Text fontWeight={'500'} color={'custom.gray.100'}>
                {t('admin.documents.report.filter.menu.title')}
              </Text>
              <Flex gap={2} alignItems="center">
                <CloseButton onClick={onFilterMenuClose} />
              </Flex>
            </Flex>
            <Divider />
            <Flex direction={'column'} gap={2}>
              <Flex justifyContent={'space-between'} w={'full'}>
                <Text fontWeight={'500'} color={'custom.gray.300'}>
                  {showPatient
                    ? t('caaMetrics.filter.fields.persona.labelPatient')
                    : t('caaMetrics.filter.fields.persona.labelTherapist')}
                </Text>
                <Button
                  variant={'link'}
                  colorScheme={'blue'}
                  color={'custom.blue.darker'}
                  onClick={() => setPersonaName('')}
                >
                  {t('caaMetrics.filter.clearFilter')}
                </Button>
              </Flex>
              <InputGroup>
                <InputLeftElement>
                  <ShearchLoupeIcon boxSize={'18px'} />
                </InputLeftElement>
                <Input
                  value={filter.personaName}
                  onChange={(e) => setPersonaName(e.currentTarget.value)}
                  borderColor={
                    filter.personaName ? 'custom.gray.400' : undefined
                  }
                  placeholder={
                    showPatient
                      ? t('caaMetrics.filter.fields.persona.placeholderPatient')
                      : t(
                          'caaMetrics.filter.fields.persona.placeholderTherapist'
                        )
                  }
                />
              </InputGroup>
            </Flex>
            {periodType === 'WEEK' ? (
              <>
                <Divider />
                <Flex direction={'column'} gap={2}>
                  <Text
                    color={'custom.gray.300'}
                    fontSize={'16px'}
                    fontWeight={500}
                  >
                    {t('caaMetrics.filter.fields.week.label')}
                  </Text>
                  <WeekInput
                    value={filter.week}
                    onChange={(v) => setWeek(v)}
                    max={moment().format(moment.HTML5_FMT.DATE)}
                  />
                </Flex>
              </>
            ) : periodType === 'MONTH' ? (
              <>
                <Divider />
                <Flex direction={'column'} gap={2}>
                  <Text
                    color={'custom.gray.300'}
                    fontSize={'16px'}
                    fontWeight={500}
                  >
                    {t('caaMetrics.filter.fields.month.label')}
                  </Text>
                  <Input
                    type={'month'}
                    value={filter.month}
                    onChange={(e) => setMonth(e.currentTarget.value)}
                    max={moment().format(moment.HTML5_FMT.MONTH)}
                    borderColor={filter.month ? 'custom.gray.400' : undefined}
                  />
                </Flex>
              </>
            ) : periodType === 'SEMESTER' ? (
              <>
                <Divider />
                <Flex direction={'column'} gap={2}>
                  <Text
                    color={'custom.gray.300'}
                    fontSize={'16px'}
                    fontWeight={500}
                  >
                    {t('caaMetrics.filter.fields.semester.label')}
                  </Text>
                  <SemesterInput
                    value={filter.semester}
                    onChange={(v) => setSemester(v)}
                  />
                </Flex>
              </>
            ) : (
              <></>
            )}
            {(periodType === 'SEMESTER' || periodType === 'YEAR') && (
              <>
                <Divider />
                <Flex direction={'column'} gap={2}>
                  <Text
                    color={'custom.gray.300'}
                    fontSize={'16px'}
                    fontWeight={500}
                  >
                    {t('caaMetrics.filter.fields.year.label')}
                  </Text>
                  <YearInput
                    value={filter.year}
                    onChange={(v) => setYear(v)}
                    max={moment().year()}
                  />
                </Flex>
              </>
            )}
          </MenuList>
        </Menu>
      </Flex>
    </Flex>
  )
}
